import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Route, Switch, useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';

import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import SegmentBar, { SegmentLink } from 'packages/kakapo-ui-kit/components/common/SegmentBar';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';

import DashboardDateSelector from './DashboardDateSelector';
import DashboardDepartmentsSelector from './DashboardDepartmentsSelector';
import DashboardEmployeesSelector from './DashboardEmployeesSelector';
import DashboardOrganizationDownloadFeedback from './DashboardDownloadFeedback';
import DashboardDownloadScores from './DashboardDownloadScores';
import { Button, Paragraph, Pill, Subheading } from '@treams/treams-ui';

const Label = styled(Box)`
	color: ${(props) => props.theme.colors.slateBlue};
	font-weight: bold;
	font-size: 14px;
`;

export default function DashboardSidebar(props: { filters: graphql.AnalyticsFilters }) {
	const { t } = useTranslation();
	const history = useHistory();
	const roles = useViewerRoles();

	const location = useLocation();

	return (
		<Box p={[3, 3, 4]}>
			<Subheading mb={3}>{t('dashboard.sidebar.filters.title')}</Subheading>
			<Route path={['/analytics/engagement']}>
				{({ match }) =>
					!match && (
						<SegmentBar mb={3}>
							{roles.isWorkspaceSuperuser && (
								<SegmentLink
									flexGrow={1}
									selected={location.pathname.endsWith('/analytics')}
									to="/analytics"
								>
									{t('dashboard.sidebar.filters.segment.organization')}
								</SegmentLink>
							)}
							{roles.isDepartmentManager && !roles.isWorkspaceSuperuser && (
								<SegmentLink
									flexGrow={1}
									selected={location.pathname.endsWith('/analytics/department')}
									to="/analytics/department"
								>
									{t('dashboard.sidebar.filters.segment.department')}
								</SegmentLink>
							)}
							<SegmentLink
								flexGrow={1}
								selected={location.pathname.endsWith('/analytics/employee')}
								to="/analytics/employee"
							>
								{t('dashboard.sidebar.filters.segment.individual')}
							</SegmentLink>
						</SegmentBar>
					)
				}
			</Route>
			<Box mb={3}>
				<Label mb={2}>{t('dashboard.sidebar.filters.timeframe.title')}:</Label>
				<DashboardDateSelector />
			</Box>
			<Switch>
				<Route path="/analytics" exact>
					<Box mb={4}>
						<Label mb={2}>{t('dashboard.sidebar.filters.departments.title')}:</Label>
						<DashboardDepartmentsSelector organization={roles.isWorkspaceSuperuser} />
					</Box>
				</Route>
				<Route
					path={[
						'/analytics/engagement',
						'/analytics/engagement/topics',
						'/analytics/engagement/questions',
					]}
					exact
				>
					<Box mb={4}>
						<Label mb={2}>{t('dashboard.sidebar.filters.departments.title')}:</Label>
						<DashboardDepartmentsSelector organization={roles.isWorkspaceSuperuser} engagement />
					</Box>
				</Route>
				{(roles.isWorkspaceSuperuser || roles.isDepartmentManager) && (
					<Route path="/analytics/employee" exact>
						<Box mb={4}>
							<Label mb={2}>{t('dashboard.sidebar.filters.employees.title')}:</Label>
							<DashboardEmployeesSelector />
						</Box>
					</Route>
				)}
				<Route path="/analytics/department" exact>
					<Box mb={4}>
						<Label mb={2}>{t('dashboard.sidebar.filters.departments.title')}:</Label>
						<DashboardDepartmentsSelector />
					</Box>
				</Route>
			</Switch>
			<Switch>
				<Route path="/analytics" exact>
					<Box mb={4}>
						<HorizontalRule />
					</Box>
					<Box mb={4}>
						<Subheading mb={3}>{t('dashboard.sidebar.cycleStatus.title')}</Subheading>
						<Flex>
							<Button
								small
								onClick={() => history.push('/inbox/manager')}
								title={t('dashboard.sidebar.cycleStatus.organization.viewProgress')}
							/>
						</Flex>
					</Box>
				</Route>
			</Switch>
			<Switch>
				<Route path={['/analytics', '/analytics/department']} exact>
					<Box mb={4}>
						<HorizontalRule />
					</Box>
					<Box mb={4}>
						<Subheading>
							{t('dashboard.sidebar.downloads.title')}{' '}
							<Pill small lightBlue ml={1}>
								{t('feature.pill.new')}
							</Pill>
						</Subheading>
						<Paragraph mb={3}>
							<a
								target="_blank"
								href="https://guides.ontreams.com/nl/articles/6385786-downloads-bij-analytics"
								rel="noreferrer"
							>
								{t('dashboard.sidebar.downloads.info.button')}
							</a>
						</Paragraph>
						<DashboardOrganizationDownloadFeedback filters={props.filters} />
						<Route path="/analytics" exact>
							{({ match }) => !!match && <DashboardDownloadScores filters={props.filters} />}
						</Route>
					</Box>
				</Route>
			</Switch>
		</Box>
	);
}
